import React from 'react';

import { Box, Typography } from '@material-ui/core';

import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { WorkerPickerRow } from '@vyce/core/src/components/WorkerPickerRow';
import { WorkersScrollList } from '@vyce/core/src/components/WorkersScrollList';
import { useCompanyWorkersList } from '@vyce/core/src/hooks/useCompanyWorkersList';
import { CompanyWorkerItem } from '@vyce/core/src/types';
import useInviteStyles from '@vyce/core/src/views/invites/styles';
import { pluralize } from '@vyce/core/src/views/invites/utils/labels';

interface Props {
  companyId: string | null;
  picked: CompanyWorkerItem | null;
  onToggle: (worker: CompanyWorkerItem) => void;
}

// Contact details under the name, so two people called John Smith can be told apart
const getContactLine = (worker: CompanyWorkerItem) =>
  [worker.email, worker.phone, worker.ni_number].filter(Boolean).join(' · ');

/** The company's own people only — admins can't look up Vyce users outside their company. */
export const ExistingAdminCandidates: React.FC<Props> = ({ companyId, picked, onToggle }) => {
  const classes = useInviteStyles();
  const { search, setSearch, workers, total, loading, loadingMore, loadMore } = useCompanyWorkersList({
    companyId: companyId || '',
    includeOnboarding: false,
  });

  if (!companyId) {
    return <Typography color="textSecondary">Select a company to see its people.</Typography>;
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={12}>
      <AppSearchInput
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder="Search by name, email, NI number or mobile..."
        isSmall
        smallRounding
        isBorder
        expanded
        fullWidth
      />

      <Box className={classes.countRow}>
        <Typography className={classes.hint}>
          {loading ? 'Loading...' : `${pluralize(total, 'person', 'people')} · pick one`}
        </Typography>
      </Box>

      <WorkersScrollList
        workers={workers}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        height="40vh"
        emptyText="No one in this company matches that search."
        renderRow={worker => (
          <WorkerPickerRow
            key={worker.user_id}
            worker={worker}
            checked={picked?.user_id === worker.user_id}
            onToggle={() => onToggle(worker)}
            subtitle={getContactLine(worker) || undefined}
          />
        )}
      />
    </Box>
  );
};
